import { captureException } from '@sentry/vue'

import { IResponseData } from '@/apis/request'

/**
 * 指示日志消息的严重性。
 * 日志级别按严重性递增的顺序排列。所以“Debug”比“Trace”等更严重。
 *
 */
export enum LogLevel {
    /** 极低严重性诊断消息的日志级别. */
    Trace,
    /** 调试错误. */
    Debug,
    /** 消息. */
    Information,
    /** 警告. */
    Warning,
    /** 错误. */
    Error,
    /** 严重错误. */
    Critical,
    /** 最高日志级别。在配置日志记录以指示不应发出日志消息时使用. */
    None
}

export interface ILogger {
    log(...msg: LogLevel | any): void
    /** 执行过程 */
    trace(...msg: any): any
    /** 调试 */
    debug(...msg: any): any
    /** 打印信息 */
    info(...msg: any): any
    /** 警告 */
    warn(...msg: any): any
    /** 异常 */
    error(...msg: any): any

    /** 异常上报 */
    captureException(error: Error, context: any): void
}

/** 日志工具 */
export default class Logger implements ILogger {
    private logLevel: LogLevel

    constructor(logLevel?: LogLevel) {
        this.logLevel = logLevel ?? LogLevel.Warning
    }

    /** get log color */
    private getLogColor(logLevel: LogLevel) {
        switch (logLevel) {
            case LogLevel.Error:
            case LogLevel.Critical:
                return '#ed4014'
            case LogLevel.Warning:
                return '#ff9900'
            case LogLevel.Information:
                return '#2d8cf0'
            case LogLevel.Debug:
                return '#19be6b'
            case LogLevel.Trace:
            default:
                return '#c5c8ce'
        }
    }

    private getLogLevelText(logLevel: LogLevel) {
        switch (logLevel) {
            case LogLevel.Critical:
                return 'critical'
            case LogLevel.Error:
                return 'error'
            case LogLevel.Warning:
                return 'warn'
            case LogLevel.Information:
                return 'info'
            case LogLevel.Debug:
                return 'debug'
            case LogLevel.Trace:
                return 'trace'
            default:
                return 'unknow'
        }
    }

    /** point log */
    log(logLevel: LogLevel, ...msg: any): void {
        if (logLevel < this.logLevel || logLevel >= LogLevel.None) return
        if (window.chrome) {
            const color: string = this.getLogColor(logLevel)
            console.log(
                `%cactivity%c${this.getLogLevelText(logLevel)}%c`,
                'background: #515a6e; color:#fff;line-height:1.5em;font-size:12px;border-radius:3px 0px 0px 3px; padding: 0 0.4em 0 0.4em;',
                `background: ${color}; color:#fff;line-height:1.5em;font-size:12px;border-radius:0px 3px 3px 0px; padding: 0 0.4em 0 0.4em;margin-right: 0.6em;`,
                '',
                ...msg
            )
        } else {
            console.log(`[activity] (${this.getLogLevelText(logLevel)}) `, ...msg)
        }
    }

    trace(...msg: any): void {
        this.log(LogLevel.Trace, ...msg)
    }

    debug(...msg: any): void {
        this.log(LogLevel.Debug, ...msg)
    }

    info(...msg: any): void {
        this.log(LogLevel.Information, ...msg)
    }

    warn(...msg: any): void {
        this.log(LogLevel.Warning, ...msg)
    }

    error(error: Error, ...msg: any) {
        this.log(LogLevel.Error, error, ...msg)
        if (!(error instanceof Error)) {
            if ((error as any).message) {
                error = new Error((error as any).message)
            } else {
                error = new Error(error)
            }
        }

        if (!this.isSkipCapture(error)) {
            if (error instanceof Error) {
                captureException(error)
            } else {
                captureException(new Error(error))
            }
        }
    }

    /** 上报异常 */
    captureException(error: Error | IResponseData, context: any): void {
        if (!this.isSkipCapture(error as Error)) {
            if ((error as IResponseData).code || (error as IResponseData).message) {
                if (!context) context = {}
                if (!context.tags) context.tags = {}
                // set tag
                context.tags = { code: (error as IResponseData).code }
                // report
                captureException(new Error(error.message), context)
            } else {
                captureException(error, context)
            }
        }
    }

    table(logLevel: LogLevel, tableData: any) {
        if (logLevel < this.logLevel || logLevel >= LogLevel.None) return
        if (window.chrome) {
            console.table(tableData)
        } else {
            console.log(JSON.stringify(tableData, null, 2))
        }
    }

    /** 是否忽略上报 */
    private isSkipCapture(error: Error): boolean {
        const skipRules: Array<string> = [
            '必填项',
            '手机号格式不正确',
            '今日验证次数超过上限，请明日重试过助力',
            '缺少必要参数',
            '请先阅读并同意服务协议!',
            '未匹配到手机号'
        ]
        // ? 检查 error message
        if (!(error instanceof Array)) {
            error = new Error(error as any)
        }

        const isSkip: boolean = !!skipRules.find((r) => new RegExp(r).test(error.message) || error.message.includes(r))
        return isSkip
    }
}
